import { createContext, useContext } from "react";
import type { AriaAttributes, ComponentType, ReactNode, Ref } from "react";

/**
 * Router-agnostic link contract. This package must not depend on any router;
 * the host app injects its own adapter via `UiLinkProvider` (the portal wraps
 * TanStack Router's `Link` in its `__root.tsx`). The type shapes below mirror
 * what router NavLink-style components accept so adapters stay drop-in.
 */
export type DiscoverBehavior = "render" | "none";

export type UiLinkTo = string | { pathname?: string; search?: string; hash?: string };

export interface UiLinkRenderState {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
}

export type UiLinkClassName = string | ((state: UiLinkRenderState) => string | undefined);

export interface UiLinkProps {
  to: UiLinkTo;
  /**
   * Router-specific link options, forwarded verbatim by the adapter and ignored by the default
   * one. `unknown` because this package must not depend on any router's types.
   */
  params?: unknown;
  search?: unknown;
  target?: string;
  /** Maps to TanStack's `resetScroll={false}` in the TanStack adapter. */
  preventScrollReset?: boolean;
  /** Router preload strategy; `false` opts out of the router's default hover-preload. */
  preload?: "intent" | "render" | "viewport" | false;
  /** react-router-specific; ignored by non-react-router adapters. */
  discover?: DiscoverBehavior;
  className?: UiLinkClassName;
  tabIndex?: number;
  "aria-hidden"?: AriaAttributes["aria-hidden"];
  ref?: Ref<HTMLAnchorElement>;
  children?: ReactNode | ((state: UiLinkRenderState) => ReactNode);
}

export type UiLinkComponent = ComponentType<UiLinkProps>;

const inactiveState: UiLinkRenderState = {
  isActive: false,
  isPending: false,
  isTransitioning: false,
};

/**
 * Default adapter: a plain anchor. Real apps always install a router adapter via
 * `UiLinkProvider`; this fallback keeps previews/stories/tests working without one.
 */
const DefaultUiLink: UiLinkComponent = ({
  to,
  params: _params,
  search: _search,
  preventScrollReset: _preventScrollReset,
  preload: _preload,
  discover: _discover,
  className,
  children,
  ...props
}) => {
  const href =
    typeof to === "string" ? to : `${to.pathname ?? ""}${to.search ?? ""}${to.hash ?? ""}`;
  return (
    <a
      {...props}
      href={href}
      className={typeof className === "function" ? className(inactiveState) : className}
    >
      {typeof children === "function" ? children(inactiveState) : children}
    </a>
  );
};

const UiLinkContext = createContext<UiLinkComponent>(DefaultUiLink);

export function UiLinkProvider({
  component,
  children,
}: {
  component: UiLinkComponent;
  children: ReactNode;
}) {
  return <UiLinkContext.Provider value={component}>{children}</UiLinkContext.Provider>;
}

export function useUiLink(): UiLinkComponent {
  return useContext(UiLinkContext);
}
